import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserStat, impersonateUser } from '../../../../services/adminService';
import toast from 'react-hot-toast';

interface UserRowProps {
    user: UserStat;
    index: number;
    onClick: () => void;
    selected: boolean;
    onToggleSelect: () => void;
}

const UserRow: React.FC<UserRowProps> = ({ user, index, onClick, selected, onToggleSelect }) => {
    const [isImpersonating, setIsImpersonating] = useState(false);

    const handleImpersonate = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isImpersonating) return;

        const confirmAccess = window.confirm(`Deseja entrar na conta de ${user.full_name || user.email}?\n\nVocê navegará com as permissões e visão exata deste usuário.`);
        if (!confirmAccess) return;

        setIsImpersonating(true);
        const toastId = toast.loading(`Acessando a conta de ${user.full_name || user.email}...`);

        try {
            const result = await impersonateUser(user.id, user.full_name || user.email || undefined);
            if (result.success) {
                toast.success('Acesso estabelecido com sucesso! Redirecionando...', { id: toastId });
                window.location.href = '/';
            } else {
                toast.error(result.error || 'Falha ao acessar conta.', { id: toastId });
                setIsImpersonating(false);
            }
        } catch {
            toast.error('Erro inesperado ao alternar para conta do usuário.', { id: toastId });
            setIsImpersonating(false);
        }
    };

    const getPlanBadge = (status: string) => {
        const badges = {
            pro: 'bg-green-500/20 text-green-600 dark:text-green-400',
            basic: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
            free: 'bg-gray-500/20 text-gray-600 dark:text-gray-400',
            inactive: 'bg-red-500/20 text-red-600 dark:text-red-400',
        };

        const labels = {
            pro: 'Pro',
            basic: 'Basic',
            free: 'Free',
            inactive: 'Inativo',
        };

        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[status as keyof typeof badges] || badges.free}`}>
                {labels[status as keyof typeof labels] || status}
            </span>
        );
    };

    const renderWhatsAppStatus = () => {
        if (!user.whatsapp_instance) {
            return <span className="text-text-dark/50 dark:text-gray-400 text-sm">Não configurado</span>;
        }

        return (
            <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${user.whatsapp_connected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm truncate max-w-[120px]" title={user.whatsapp_instance}>
                    {user.whatsapp_instance}
                </span>
            </div>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`flex items-center gap-4 px-6 py-4 hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors cursor-pointer border-b border-border-light/50 dark:border-border-dark/50 ${selected ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
            onClick={onClick}
        >
            <input
                type="checkbox"
                checked={selected}
                onChange={onToggleSelect}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-gray-400 text-primary focus:ring-primary flex-shrink-0"
                aria-label={`Selecionar ${user.full_name || user.email || 'usuário'}`}
            />
            <div className="grid grid-cols-12 gap-4 flex-1 min-w-0">
            {/* Nome */}
            <div className="col-span-2 flex items-center min-w-0">
                <div className="flex items-center gap-3 min-w-0 w-full">
                    <div className="w-10 h-10 rounded-full bg-primary/30 dark:bg-primary/40 flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-primary">person</span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-medium text-text-dark dark:text-white truncate" title={user.full_name || 'Sem nome'}>
                            {user.full_name || 'Sem nome'}
                        </p>
                        <p className="text-xs text-text-dark/60 dark:text-gray-400 truncate" title={user.email || 'Sem email'}>
                            {user.email || 'Sem email'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Plano */}
            <div className="col-span-2 flex items-center">
                {getPlanBadge(user.subscription_status)}
            </div>

            {/* Leads Totais */}
            <div className="col-span-1 flex items-center">
                <span className="font-semibold text-text-dark dark:text-white">
                    {user.total_leads}
                </span>
            </div>

            {/* Leads Prospectados */}
            <div className="col-span-1 flex items-center">
                <span className="font-semibold text-text-dark dark:text-white">
                    {user.prospected_leads}
                </span>
            </div>

            {/* Pesquisas */}
            <div className="col-span-2 flex items-center">
                <span className="font-semibold text-text-dark dark:text-white">
                    {user.total_searches}
                </span>
            </div>

            {/* WhatsApp */}
            <div className="col-span-2 flex items-center">
                {renderWhatsAppStatus()}
            </div>

            {/* Tokens */}
            <div className="col-span-1 flex items-center">
                <span className="text-sm text-text-dark dark:text-white">
                    {user.total_tokens_used.toLocaleString()}
                </span>
            </div>

            {/* Ações */}
            <div className="col-span-1 flex items-center justify-end gap-2">
                <button
                    onClick={handleImpersonate}
                    disabled={isImpersonating}
                    title="Entrar na conta deste usuário (Visão do Usuário)"
                    className="p-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500 text-amber-600 dark:text-amber-400 hover:text-white transition-all flex items-center justify-center disabled:opacity-50"
                >
                    {isImpersonating ? (
                        <span className="material-symbols-outlined text-sm animate-spin">sync</span>
                    ) : (
                        <span className="material-symbols-outlined text-sm">switch_account</span>
                    )}
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                    }}
                    title="Ver detalhes do usuário"
                    className="p-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors flex items-center justify-center"
                >
                    <span className="material-symbols-outlined text-sm">visibility</span>
                </button>
            </div>
            </div>
        </motion.div>
    );
};

export default UserRow;
